import { useState, useMemo, useEffect, useCallback } from 'react';
import Loading from '@/components/general/Loading';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import checkinReports, { PostCheckinFeedbacks, MarkAsReviewed } from '@/services/checkinReports';
import notification from '@/services/notification';
import unreviewedCheckinsServices, {
  PendingUsersCheckins,
  UnreviewedCheckinsByTeamsResponse,
} from '@/services/unreviewedCheckins';
import { FetchApiIds, UnreviewedCheckinsTabsKeys } from '@/types';
import {
  UserCheckin,
  Feedback,
  CheckinSummary,
  NotificationGroups,
  NotificationParams,
} from '@/types/api';
import CheckinsUnreviewedComponent from './CheckinsUnreviewed.component';

const CheckinsUnreviewedPage = function (): JSX.Element {
  const { profile } = useProfile();
  const toast = useToastContext();
  const [isReviewing, setIsReviewing] = useState<boolean>(false);
  const [checkinsToBeReviewed, setCheckinsToBeReviewed] = useState<CheckinSummary[]>([]);
  const [view, setView] = useState<UnreviewedCheckinsTabsKeys>(UnreviewedCheckinsTabsKeys.TEAM);

  const pendingCheckinsResponse = useApiFetch<PendingUsersCheckins>(
    FetchApiIds.GET_ALL_PENDING_CHECKINS,
    unreviewedCheckinsServices.getEveryonePendingCheckins
  );

  const unreviewedCheckinsResponse = useApiFetch<CheckinSummary[]>(
    FetchApiIds.UNREVIEWED_CHECKINS,
    unreviewedCheckinsServices.getAllCheckinsUnreviewed
  );

  const unreviewedCheckinsByTeamsResponse = useApiFetch<UnreviewedCheckinsByTeamsResponse>(
    FetchApiIds.UNREVIEWED_CHECKINS_BY_TEAM,
    unreviewedCheckinsServices.getAllCheckinsUnreviewedByTeam
  );

  const submitFeedbackMutation = useApiSubmit<Feedback, PostCheckinFeedbacks>(
    checkinReports.postCheckinFeedbacks
  );

  const reviewCheckinMutation = useApiSubmit<void, MarkAsReviewed>(checkinReports.markAsReviewed);

  const scrollToTop = (): void => {
    const parent = document.getElementById('page-content');
    if (parent) {
      parent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const successToastNotification = (): void => {
    toast.setMessage('Notifications sent! Hooray!');
    toast.setSeverity('success');
    toast.showToast();
  };

  const failureToastNotification = useCallback((): void => {
    toast.setMessage('Something went wrong! Please try again.');
    toast.setSeverity('error');
    toast.showToast();
  }, [toast]);

  const firstReviewingCheckinId = checkinsToBeReviewed[0]?.id;

  const checkinReviewingQuery = useApiFetch<UserCheckin>(
    [FetchApiIds.CHECKIN_BY_ID, firstReviewingCheckinId],
    async () => checkinReports.getCheckinById({ checkinId: firstReviewingCheckinId }),
    { enabled: !!firstReviewingCheckinId }
  );

  useEffect(() => {
    if (isReviewing && checkinsToBeReviewed.length === 0) {
      toast.setMessage('All check-ins have been reviewed.');
      toast.showToast();
      setIsReviewing(false);
      unreviewedCheckinsResponse.refetch();
      unreviewedCheckinsByTeamsResponse.refetch();
    }
  }, [
    isReviewing,
    unreviewedCheckinsByTeamsResponse,
    unreviewedCheckinsResponse,
    toast,
    checkinsToBeReviewed.length,
  ]);

  const handlePostFeedback = async (checkinId: string, feedback?: string): Promise<void> => {
    if (profile.id && feedback) {
      const payload = {
        text: feedback,
        userId: profile.id,
      };

      await submitFeedbackMutation.mutateAsync({ checkinId, feedback: payload });
      checkinReviewingQuery.refetch();
    }
  };

  const handleMarkAsReviewed = async (checkinId: string): Promise<void> => {
    await reviewCheckinMutation.mutateAsync({ checkinId, checkinReviewed: { isReviewed: true } });
    scrollToTop();
    setCheckinsToBeReviewed((checkins) => checkins.filter((checkin) => checkin.id !== checkinId));
  };

  const handleChangeView = (newView: UnreviewedCheckinsTabsKeys): void => {
    setView(newView);
  };

  const handleStartReviewingCheckins = (groupOfCheckins: CheckinSummary[]): void => {
    const answeredCheckins = groupOfCheckins.filter((checkin) => !!checkin.answeredAt);
    scrollToTop();
    setIsReviewing(true);
    setCheckinsToBeReviewed(answeredCheckins);
  };

  const notificationMutation = useApiSubmit<void, NotificationParams>(
    notification.sendNotifications
  );

  const handleNotifyEveryone = async (): Promise<void> => {
    try {
      await notificationMutation.mutateAsync({
        type: 'CHECKIN_PENDING',
        data: {
          group: NotificationGroups.everyone,
        },
      });
      successToastNotification();
    } catch (err) {
      failureToastNotification();
    }
  };

  const handleNotifyTeam = async (teamId: string): Promise<void> => {
    try {
      await notificationMutation.mutateAsync({
        type: 'CHECKIN_PENDING',
        data: {
          group: NotificationGroups.team,
          teamId,
        },
      });
      successToastNotification();
    } catch (err) {
      failureToastNotification();
    }
  };

  const checkinsList = useMemo(() => {
    const currentCheckin = checkinReviewingQuery?.data ?? null;
    const nextCheckins = checkinsToBeReviewed.slice(1);
    return {
      currentCheckin,
      nextCheckins,
    };
  }, [checkinsToBeReviewed, checkinReviewingQuery?.data]);

  const checkinsByTeamsFiltered = useMemo(() => {
    const checkinsByTeams = unreviewedCheckinsByTeamsResponse.data?.teams ?? [];
    return checkinsByTeams;
  }, [unreviewedCheckinsByTeamsResponse.data?.teams]);

  const totalPeopleToBeReminded = useMemo(
    () => pendingCheckinsResponse?.data?.totalOfPendingCheckins ?? 0,
    [pendingCheckinsResponse.data]
  );

  const isPageLoading = useMemo(
    () =>
      pendingCheckinsResponse.isLoading ||
      unreviewedCheckinsResponse.isLoading ||
      unreviewedCheckinsByTeamsResponse.isLoading,
    [
      pendingCheckinsResponse.isLoading,
      unreviewedCheckinsResponse.isLoading,
      unreviewedCheckinsByTeamsResponse.isLoading,
    ]
  );

  const checkinsHistory = useMemo(() => {
    const { currentCheckin, nextCheckins } = checkinsList;
    const currentCheckinFormatted = {
      answeredAt: currentCheckin?.answeredAt,
      dueDate: currentCheckin?.dueDate.endDate,
      id: currentCheckin?.id,
      isReviewed: currentCheckin?.isReviewed,
      userName: currentCheckin?.user.name,
    } as CheckinSummary;

    return [...(currentCheckin ? [currentCheckinFormatted] : []), ...nextCheckins];
  }, [checkinsList]);

  if (isPageLoading) return <Loading fullPage />;

  return (
    <CheckinsUnreviewedComponent
      view={view}
      isReviewing={isReviewing}
      checkinsByTeamsList={checkinsByTeamsFiltered}
      currentCheckin={checkinsList.currentCheckin as UserCheckin}
      nextCheckins={checkinsList.nextCheckins}
      handleChangeView={handleChangeView}
      handleStartReviewingCheckins={handleStartReviewingCheckins}
      handlePostFeedback={handlePostFeedback}
      handleMarkAsReviewed={handleMarkAsReviewed}
      checkinsHistory={checkinsHistory}
      refetchCheckins={checkinReviewingQuery.refetch}
      AllUnreviewedCheckins={unreviewedCheckinsResponse.data ?? []}
      totalPeopleToBeReminded={totalPeopleToBeReminded}
      handleNotifyEveryone={handleNotifyEveryone}
      handleNotifyTeam={handleNotifyTeam}
    />
  );
};

export default CheckinsUnreviewedPage;
